import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";
import AdminSaveBar from "@/components/feature/AdminSaveBar";
import SEOHead from "@/components/feature/SEOHead";
import { SharePanel } from "@/components/feature/SharePanel";
import PreviewModeBar from "@/components/feature/PreviewModeBar";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { loadSubmittedReviews, type SubmittedReview, SUBMITTED_REVIEWS_KEY } from "@/pages/leave-a-review/page";
import { stagingStoreSet, stagingStoreNotify } from "@/hooks/useAdminStorage";
import { EDGE_FUNCTIONS, getEdgeFunctionHeaders } from "@/utils/edgeFunctionUrl";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Testimonial {
  id: string;
  name: string;
  role: string;
  medium: string;
  quote: string;
  rating: number;
  image: string;
  featured: boolean;
  hidden: boolean;
  imagePosition?: string;
}

interface TestimonialsData {
  pageVisible: boolean;
  eyebrow: string;
  heading: string;
  subheading: string;
  testimonials: Testimonial[];
  ctaLabel: string;
  ctaSubtext: string;
  // Editable section labels
  makersHeading: string;
  visitorsHeading: string;
}

const DEFAULT_DATA: TestimonialsData = {
  pageVisible: false,
  eyebrow: "Artist Reviews",
  heading: "What Our Makers Are Saying",
  subheading: "Real stories from the artists and vendors who call Makers Alley Co. home.",
  ctaLabel: "Share Your Experience",
  ctaSubtext: "Are you an artist or customer? We'd love to hear from you.",
  makersHeading: "What Our Makers Are Saying",
  visitorsHeading: "From Our Visitors",
  testimonials: [
    {
      id: "t1",
      name: "Luna Ceramics",
      role: "Artist Vendor",
      medium: "Ceramics & Pottery",
      quote: "I spent years hauling my ceramics to weekend markets, packing up Sunday night, and starting over. Makers Alley Co. changed everything. My work is on display every single day, and I keep every dollar of every sale. It's the permanent home I always dreamed of.",
      rating: 5,
      image: "",
      featured: true,
      hidden: false,
    },
    {
      id: "t2",
      name: "Wildflower Press",
      role: "Artist Vendor",
      medium: "Art Prints & Illustration",
      quote: "The algorithm was killing my Etsy shop. Here, real people walk in, discover my prints, and buy them — no SEO tricks, no ad spend. The staff genuinely knows my work and talks about it with customers. That's priceless.",
      rating: 5,
      image: "",
      featured: true,
      hidden: false,
    },
    {
      id: "t3",
      name: "Ember & Thread",
      role: "Artist Vendor",
      medium: "Fiber Art & Textiles",
      quote: "I was skeptical at first — could a retail space really replace the energy of a craft fair? Absolutely yes. My macramé pieces sell consistently year-round now, and I don't have to set up a booth at 6am on a cold Saturday morning.",
      rating: 5,
      image: "",
      featured: false,
      hidden: false,
    },
    {
      id: "t4",
      name: "Sage & Stone Jewelry",
      role: "Artist Vendor",
      medium: "Handmade Jewelry",
      quote: "The application process was thorough and fair. I appreciated that they actually care about the quality and authenticity of the work. Being part of a curated collective of makers has elevated my brand in ways I didn't expect.",
      rating: 5,
      image: "",
      featured: false,
      hidden: false,
    },
    {
      id: "t5",
      name: "Rogue Valley Candle Co.",
      role: "Artist Vendor",
      medium: "Soy Candles & Home Goods",
      quote: "I love that the staff actually uses and knows my products. When a customer asks about the candles, they can answer questions I'd answer myself. It feels like having a team that genuinely cares about my small business.",
      rating: 5,
      image: "",
      featured: false,
      hidden: false,
    },
    {
      id: "t6",
      name: "A Happy Shopper",
      role: "Customer",
      medium: "Regular Visitor",
      quote: "I discovered this shop looking for a unique gift and ended up buying something for every person on my list. Everything is handmade and local — you can feel the care that went into each piece. I'll be back every season.",
      rating: 5,
      image: "",
      featured: false,
      hidden: false,
    },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Determine which section a submitted review belongs to based on role */
function getReviewSection(role: string): "makers" | "visitors" {
  const r = role.toLowerCase();
  if (r.includes("artist") || r.includes("vendor") || r.includes("maker")) return "makers";
  return "visitors";
}

// ─── StarRating ───────────────────────────────────────────────────────────────

function StarRating({ rating, editable, onChange }: { rating: number; editable?: boolean; onChange?: (r: number) => void }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => editable && onChange && onChange(star)}
          className={`text-lg ${editable ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"} ${star <= rating ? "text-amber-400" : "text-gray-300"}`}
        >
          <i className="ri-star-fill"></i>
        </button>
      ))}
    </div>
  );
}

// ─── SortableTestimonialCard ──────────────────────────────────────────────────

interface CardProps {
  testimonial: Testimonial;
  isAdmin: boolean;
  isDark: boolean;
  large: boolean;
  onUpdate: (id: string, field: keyof Testimonial, val: string | number | boolean) => void;
  onRemove: (id: string) => void;
  headingColor: string;
  subColor: string;
  cardBg: string;
}

function SortableTestimonialCard(props: CardProps) {
  const { testimonial: t, isAdmin } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: t.id, disabled: !isAdmin });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TestimonialCardInner {...props} dragHandleProps={isAdmin ? { ...attributes, ...listeners } : undefined} />
    </div>
  );
}

interface CardInnerProps extends CardProps {
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
}

function TestimonialCardInner({ testimonial: t, isAdmin, isDark, large, onUpdate, onRemove, headingColor, subColor, cardBg, dragHandleProps }: CardInnerProps) {
  const isHidden = t.hidden;

  return (
    <div className={`rounded-2xl border p-6 flex flex-col gap-4 relative group/card transition-all ${cardBg} ${large ? "lg:flex-row lg:gap-6" : ""} ${isHidden && isAdmin ? "opacity-50" : ""}`}>
      {/* Admin controls */}
      {isAdmin && (
        <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover/card:opacity-100 transition-opacity z-10">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full cursor-grab active:cursor-grabbing transition-colors"
            title="Drag to reorder"
          >
            <i className="ri-drag-move-2-line text-xs"></i>
          </div>
          {/* Feature toggle */}
          <button
            onClick={() => onUpdate(t.id, "featured", !t.featured)}
            className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
              t.featured ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
            title={t.featured ? "Remove from featured" : "Mark as featured"}
          >
            <i className={t.featured ? "ri-star-fill" : "ri-star-line"}></i>
          </button>
          {/* Hide/show toggle */}
          <button
            onClick={() => onUpdate(t.id, "hidden", !t.hidden)}
            className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
              isHidden ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
            title={isHidden ? "Show this review" : "Hide this review"}
          >
            <i className={isHidden ? "ri-eye-line" : "ri-eye-off-line"}></i>
          </button>
          {/* Delete */}
          <button
            onClick={() => onRemove(t.id)}
            className="w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors"
            title="Remove"
          >
            <i className="ri-delete-bin-line text-xs"></i>
          </button>
        </div>
      )}

      {/* Hidden badge */}
      {isAdmin && isHidden && (
        <div className="absolute top-3 left-3 bg-gray-400 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
          Hidden
        </div>
      )}

      {/* Photo */}
      <div className={`flex-shrink-0 ${large ? "w-full lg:w-32 h-32" : "w-16 h-16"} rounded-2xl overflow-hidden`}>
        {isAdmin ? (
          <AdminImageUpload
            src={t.image}
            alt={t.name}
            onReplace={(url) => onUpdate(t.id, "image", url)}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
            objectPosition={t.imagePosition ?? "50% 50%"}
            onPositionChange={(pos) => onUpdate(t.id, "imagePosition", pos)}
            mobileObjectPosition={(t as typeof t & { mobileImagePosition?: string }).mobileImagePosition ?? t.imagePosition ?? "50% 50%"}
            onMobilePositionChange={(pos) => onUpdate(t.id, "mobileImagePosition", pos)}
            isDark={isDark}
          />
        ) : (
          t.image ? (
            <img
              src={t.image}
              alt={t.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: t.imagePosition ?? "50% 50%" }}
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
              <i className={`ri-user-line text-pink-400 ${large ? "text-3xl" : "text-xl"}`}></i>
            </div>
          )
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <StarRating
            rating={t.rating}
            editable={isAdmin}
            onChange={(r) => onUpdate(t.id, "rating", r)}
          />
          <SharePanel
            review={{ name: t.name, role: t.role, quote: t.quote, rating: t.rating }}
            className="ml-2"
          />
        </div>
        <div className="mt-3 mb-4">
          {isAdmin ? (
            <AdminEditField value={t.quote} onSave={(v) => onUpdate(t.id, "quote", v)} multiline inputClassName="text-sm italic leading-relaxed">
              <p className={`text-sm italic leading-relaxed ${subColor}`}>"{t.quote}"</p>
            </AdminEditField>
          ) : (
            <p className={`text-sm italic leading-relaxed ${subColor}`}>"{t.quote}"</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-0.5 rounded-full bg-pink-400 flex-shrink-0"></div>
          <div>
            {isAdmin ? (
              <>
                <AdminEditField value={t.name} onSave={(v) => onUpdate(t.id, "name", v)} inputClassName="font-bold text-sm">
                  <div className={`font-bold text-sm ${headingColor}`}>{t.name}</div>
                </AdminEditField>
                <div className="flex gap-1.5 mt-0.5">
                  <AdminEditField value={t.role} onSave={(v) => onUpdate(t.id, "role", v)} inputClassName="text-xs text-pink-500">
                    <span className="text-xs text-pink-500 font-semibold">{t.role}</span>
                  </AdminEditField>
                  <span className={`text-xs ${subColor}`}>·</span>
                  <AdminEditField value={t.medium} onSave={(v) => onUpdate(t.id, "medium", v)} inputClassName="text-xs">
                    <span className={`text-xs ${subColor}`}>{t.medium}</span>
                  </AdminEditField>
                </div>
              </>
            ) : (
              <>
                <div className={`font-bold text-sm ${headingColor}`}>{t.name}</div>
                <div className="flex gap-1.5 mt-0.5">
                  <span className="text-xs text-pink-500 font-semibold">{t.role}</span>
                  <span className={`text-xs ${subColor}`}>·</span>
                  <span className={`text-xs ${subColor}`}>{t.medium}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SubmittedReviewCard ──────────────────────────────────────────────────────

function SubmittedReviewCard({ review, isDark, headingColor, subColor, isAdmin, large, onToggle, onDelete, onMoveSection, onUpdatePosition, onToggleFeatured }: {
  review: SubmittedReview;
  isDark: boolean;
  headingColor: string;
  subColor: string;
  isAdmin: boolean;
  large?: boolean;
  onToggle?: () => void;
  onDelete?: () => void;
  onMoveSection?: () => void;
  onUpdatePosition?: (pos: string) => void;
  onToggleFeatured?: () => void;
}) {
  return (
    <div className={`rounded-2xl border p-6 flex flex-col gap-4 relative group/submitted transition-all ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100"} ${large ? "lg:flex-row lg:gap-6" : ""} ${!review.visible && isAdmin ? "opacity-50" : ""}`}>
      {isAdmin && (
        <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover/submitted:opacity-100 transition-opacity z-10">
          {onToggleFeatured && (
            <button
              onClick={onToggleFeatured}
              className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                review.featured ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              title={review.featured ? "Un-feature" : "Feature this review"}
            >
              <i className={review.featured ? "ri-star-fill" : "ri-star-line"}></i>
            </button>
          )}
          {onMoveSection && (
            <button
              onClick={onMoveSection}
              className="text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors bg-amber-100 text-amber-700 hover:bg-amber-200"
              title="Move to other section"
            >
              <i className="ri-arrow-left-right-line text-xs"></i> Move
            </button>
          )}
          {onToggle && (
            <button
              onClick={onToggle}
              className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                review.visible ? "bg-gray-100 text-gray-500 hover:bg-gray-200" : "bg-green-100 text-green-700 hover:bg-green-200"
              }`}
              title={review.visible ? "Hide" : "Show"}
            >
              <i className={review.visible ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              className="w-7 h-7 flex items-center justify-center bg-red-100 hover:bg-red-500 text-red-400 hover:text-white rounded-full cursor-pointer transition-colors"
              title="Delete"
            >
              <i className="ri-delete-bin-line text-xs"></i>
            </button>
          )}
        </div>
      )}
      {isAdmin && !review.visible && (
        <div className="absolute top-3 left-3 bg-gray-400 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">Hidden</div>
      )}
      {isAdmin && review.featured && (
        <div className="absolute top-3 left-3 bg-amber-400 text-amber-900 text-[9px] font-bold px-2 py-0.5 rounded-full">Featured</div>
      )}

      {/* Photo — large for featured, small for regular */}
      <div className={`flex-shrink-0 rounded-2xl overflow-hidden ${large ? "w-full lg:w-32 h-32" : "w-16 h-16"}`}>
        {review.image ? (
          isAdmin && onUpdatePosition ? (
            <AdminImageUpload
              src={review.image}
              alt={review.name}
              onReplace={() => { /* read-only for submitted reviews */ }}
              className="w-full h-full"
              imgClassName="w-full h-full object-cover"
              objectPosition={review.imagePosition ?? "50% 50%"}
              onPositionChange={onUpdatePosition}
              isDark={isDark}
            />
          ) : (
            <img
              src={review.image}
              alt={review.name}
              className="w-full h-full object-cover"
              style={{ objectPosition: review.imagePosition ?? "50% 50%" }}
            />
          )
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
            <i className={`ri-user-line text-pink-400 ${large ? "text-3xl" : "text-xl"}`}></i>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex gap-0.5">
            {[1,2,3,4,5].map((s) => (
              <i key={s} className={`ri-star-fill ${large ? "text-lg" : "text-base"} ${s <= review.rating ? "text-amber-400" : "text-gray-300"}`}></i>
            ))}
          </div>
          <SharePanel
            review={{ name: review.name, role: review.role, quote: review.quote, rating: review.rating }}
            className="ml-2"
          />
        </div>
        <p className={`italic leading-relaxed flex-1 ${large ? "text-base" : "text-sm"} ${isDark ? "text-gray-300" : "text-gray-500"}`}>"{review.quote}"</p>
        <div className="flex items-center gap-3">
          <div className="w-8 h-0.5 rounded-full bg-pink-400 flex-shrink-0"></div>
          <div>
            <div className={`font-bold text-sm ${isDark ? "text-white" : "text-gray-900"}`}>{review.name}</div>
            <div className="flex gap-1.5 mt-0.5 flex-wrap">
              <span className="text-xs text-pink-500 font-semibold">{review.role}</span>
              {review.medium && (
                <><span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>·</span>
                <span className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>{review.medium}</span></>
              )}
            </div>
          </div>
        </div>
        <p className={`text-xs ${isDark ? "text-gray-600" : "text-gray-400"}`}>
          {new Date(review.submittedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </p>
      </div>
    </div>
  );
}

// ─── Section Divider ──────────────────────────────────────────────────────────

function SectionDivider({ label, isAdmin, onEdit, isDark }: {
  label: string;
  isAdmin: boolean;
  onEdit: (v: string) => void;
  isDark: boolean;
}) {
  const subColor = isDark ? "text-gray-500" : "text-gray-400";
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className={`h-px flex-1 ${isDark ? "bg-gray-700" : "bg-gray-200"}`}></div>
      {isAdmin ? (
        <AdminEditField value={label} onSave={onEdit} inputClassName={`text-xs font-bold uppercase tracking-widest text-center ${subColor}`}>
          <span className={`text-xs font-bold uppercase tracking-widest px-3 ${subColor}`}>{label}</span>
        </AdminEditField>
      ) : (
        <span className={`text-xs font-bold uppercase tracking-widest px-3 ${subColor}`}>{label}</span>
      )}
      <div className={`h-px flex-1 ${isDark ? "bg-gray-700" : "bg-gray-200"}`}></div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TestimonialsPage() {
  const { isAdmin, isEditing } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const navigate = useNavigate();
  const [data, setData] = useAdminStorage<TestimonialsData>("ma_testimonials_v2", DEFAULT_DATA);

  useEffect(() => {
    if (!data.pageVisible && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [data.pageVisible, isAdmin, navigate]);

  // Live submitted reviews — fetched from Supabase directly for real-time cross-browser sync
  const [submittedReviews, setSubmittedReviews] = useState<SubmittedReview[]>(() => loadSubmittedReviews());

  // Fetch reviews from Supabase edge function on mount and periodically
  useEffect(() => {
    const fetchFromSupabase = async () => {
      try {
        const res = await fetch(EDGE_FUNCTIONS.getSiteContent, {
          method: "GET",
          headers: getEdgeFunctionHeaders(),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => res.statusText);
          console.error(`[testimonials] get-site-content returned HTTP ${res.status}: ${text}`);
          setSubmittedReviews(loadSubmittedReviews());
          return;
        }
        const json = await res.json() as { content?: Record<string, unknown> };
        const reviews = json.content?.[SUBMITTED_REVIEWS_KEY] as SubmittedReview[] | undefined;
        if (reviews && Array.isArray(reviews)) {
          setSubmittedReviews(reviews);
          try { localStorage.setItem(SUBMITTED_REVIEWS_KEY, JSON.stringify(reviews)); } catch { /* ignore */ }
        } else {
          setSubmittedReviews(loadSubmittedReviews());
        }
      } catch (err) {
        console.error("[testimonials] Failed to fetch reviews from Supabase:", err);
        setSubmittedReviews(loadSubmittedReviews());
      }
    };

    fetchFromSupabase();
    const interval = setInterval(fetchFromSupabase, 30000);
    return () => clearInterval(interval);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const set = <K extends keyof TestimonialsData>(key: K, val: TestimonialsData[K]) => {
    setData({ ...data, [key]: val });
  };

  const updateTestimonial = useCallback((id: string, field: keyof Testimonial, val: string | number | boolean) => {
    setData({ ...data, testimonials: data.testimonials.map((t) => t.id === id ? { ...t, [field]: val } : t) });
  }, [data, setData]);

  const addTestimonial = () => {
    const newT: Testimonial = {
      id: `t-${Date.now()}`,
      name: "New Reviewer",
      role: "Artist Vendor",
      medium: "Medium / Category",
      quote: "Click to edit this testimonial quote.",
      rating: 5,
      image: "",
      featured: false,
      hidden: false,
    };
    setData({ ...data, testimonials: [...data.testimonials, newT] });
  };

  const removeTestimonial = useCallback((id: string) => {
    setData({ ...data, testimonials: data.testimonials.filter((t) => t.id !== id) });
  }, [data, setData]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = data.testimonials.findIndex((t) => t.id === active.id);
    const newIndex = data.testimonials.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setData({ ...data, testimonials: arrayMove(data.testimonials, oldIndex, newIndex) });
  };

  // Admin: toggle/delete/move submitted reviews — sync to Supabase
  const syncReviewsToSupabase = async (reviews: SubmittedReview[]) => {
    // Always update local first
    try { localStorage.setItem(SUBMITTED_REVIEWS_KEY, JSON.stringify(reviews)); } catch { /* ignore */ }
    stagingStoreSet(SUBMITTED_REVIEWS_KEY, reviews);
    stagingStoreNotify(SUBMITTED_REVIEWS_KEY);

    // Also push to Supabase immediately so changes are universal
    try {
      const res = await fetch(EDGE_FUNCTIONS.saveSiteContent, {
        method: "POST",
        headers: {
          ...getEdgeFunctionHeaders(),
          "x-admin-token": "makersalley2024",
        },
        body: JSON.stringify({
          updates: { [SUBMITTED_REVIEWS_KEY]: reviews },
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        console.error(`[testimonials] save-site-content returned HTTP ${res.status}: ${text}`);
      }
    } catch (err) {
      console.error("[testimonials] Failed to sync reviews to Supabase:", err);
    }
  };

  const updateSubmittedReview = (id: string, changes: Partial<SubmittedReview>) => {
    const next = submittedReviews.map((r) => r.id === id ? { ...r, ...changes } : r);
    setSubmittedReviews(next);
    syncReviewsToSupabase(next);
  };

  const deleteSubmittedReview = (id: string) => {
    const next = submittedReviews.filter((r) => r.id !== id);
    setSubmittedReviews(next);
    syncReviewsToSupabase(next);
  };

  const moveReviewSection = (id: string) => {
    const review = submittedReviews.find((r) => r.id === id);
    if (!review) return;
    const currentSection = review.sectionOverride ?? getReviewSection(review.role);
    const newSection = currentSection === "makers" ? "visitors" : "makers";
    updateSubmittedReview(id, { sectionOverride: newSection });
  };

  const bg = isDark ? "bg-gray-950" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-300" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";

  if (!data.pageVisible && !isAdmin) {
    return null;
  }

  const visibleTestimonials = isAdmin ? data.testimonials : data.testimonials.filter((t) => !t.hidden);
  const featured = visibleTestimonials.filter((t) => t.featured);
  const rest = visibleTestimonials.filter((t) => !t.featured);

  // Split submitted reviews by section
  const visibleSubmitted = isAdmin ? submittedReviews : submittedReviews.filter((r) => r.visible);
  const makersReviews = visibleSubmitted.filter((r) => {
    const section = (r as SubmittedReview & { sectionOverride?: string }).sectionOverride ?? getReviewSection(r.role);
    return section === "makers";
  });
  const visitorsReviews = visibleSubmitted.filter((r) => {
    const section = (r as SubmittedReview & { sectionOverride?: string }).sectionOverride ?? getReviewSection(r.role);
    return section === "visitors";
  });

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${bg}`} style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEOHead
        title="Artist Reviews & Testimonials — Makers Alley Co"
        description="Read real reviews from artists and customers at Makers Alley Co. Discover what independent makers and shoppers love about our handmade retail space in Medford, Oregon."
        keywords="Makers Alley reviews, artist testimonials, handmade shop reviews, Medford Oregon art shop, maker marketplace reviews"
        canonicalPath="/testimonials"
        ogType="website"
        ogTitle="Artist Reviews & Testimonials — Makers Alley Co"
        ogDescription="Real stories from the artists and customers who love Makers Alley Co. Discover what makes our handmade retail space special."
        lastModified="2026-05-10"
        author="Makers Alley Co"
      />
      <PreviewModeBar />
      <Navbar />

      {isAdmin && !data.pageVisible && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3 text-center mt-14">
          <p className="text-yellow-700 text-sm font-semibold">
            <i className="ri-eye-off-line mr-1"></i>
            This page is completely hidden from visitors. Toggle visibility below to publish it.
          </p>
        </div>
      )}

      {isAdmin && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-center">
          <p className="text-amber-700 text-xs font-semibold">
            <i className="ri-drag-move-2-line mr-1"></i>
            Admin mode: Hover any card to drag-reorder, hide/show, feature, or delete. Visitor-submitted reviews auto-route by role — use the Move button to reassign sections.
          </p>
        </div>
      )}

      {/* Hero */}
      <div
        className={`pt-20 pb-16 px-6 text-center ${isDark ? "bg-gray-800" : ""}`}
        style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}
      >
        <div className="max-w-3xl mx-auto">
          {isEditing ? (
            <AdminEditField value={data.eyebrow} onSave={(v) => set("eyebrow", v)} className="mb-2" inputClassName="text-green-600 text-sm font-bold uppercase tracking-widest text-center">
              <span className="text-green-600 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
            </AdminEditField>
          ) : (
            <span className="text-green-600 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
          )}
          {isEditing ? (
            <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} className="mt-3 mb-4" inputClassName="text-5xl font-extrabold text-center">
              <h1 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h1>
            </AdminEditField>
          ) : (
            <h1 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.heading}</h1>
          )}
          {isEditing ? (
            <AdminEditField value={data.subheading} onSave={(v) => set("subheading", v)} multiline inputClassName="text-lg text-center">
              <p className={`text-lg max-w-2xl mx-auto ${subColor}`}>{data.subheading}</p>
            </AdminEditField>
          ) : (
            <p className={`text-lg max-w-2xl mx-auto ${subColor}`}>{data.subheading}</p>
          )}
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-16">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={data.testimonials.map((t) => t.id)} strategy={rectSortingStrategy}>
            {/* Featured testimonials */}
            {(featured.length > 0 || isAdmin) && (
              <div className="mb-14">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {featured.map((t) => (
                    <SortableTestimonialCard
                      key={t.id}
                      testimonial={t}
                      isAdmin={isAdmin}
                      isDark={isDark}
                      large
                      onUpdate={updateTestimonial}
                      onRemove={removeTestimonial}
                      headingColor={headingColor}
                      subColor={subColor}
                      cardBg={cardBg}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Regular testimonials grid */}
            {(rest.length > 0 || isAdmin) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((t) => (
                  <SortableTestimonialCard
                    key={t.id}
                    testimonial={t}
                    isAdmin={isAdmin}
                    isDark={isDark}
                    large={false}
                    onUpdate={updateTestimonial}
                    onRemove={removeTestimonial}
                    headingColor={headingColor}
                    subColor={subColor}
                    cardBg={cardBg}
                  />
                ))}
                {isAdmin && (
                  <button
                    onClick={addTestimonial}
                    className={`rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-3 p-8 cursor-pointer transition-all hover:border-pink-400 hover:bg-pink-50/50 min-h-[200px] ${
                      isDark ? "border-gray-600 text-gray-500 hover:text-pink-400" : "border-pink-200 text-gray-400 hover:text-pink-500"
                    }`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-100">
                      <i className="ri-add-line text-pink-500 text-2xl"></i>
                    </div>
                    <span className="text-sm font-semibold">Add Testimonial</span>
                  </button>
                )}
              </div>
            )}
          </SortableContext>
        </DndContext>

        {/* Makers section — submitted reviews from artists/vendors */}
        {(makersReviews.length > 0 || (isAdmin && submittedReviews.some((r) => {
          const s = (r as SubmittedReview & { sectionOverride?: string }).sectionOverride ?? getReviewSection(r.role);
          return s === "makers";
        }))) && (
          <div className="mt-14">
            <SectionDivider
              label={data.makersHeading ?? "What Our Makers Are Saying"}
              isAdmin={isAdmin}
              onEdit={(v) => set("makersHeading", v)}
              isDark={isDark}
            />
            {/* Featured submitted reviews — large 2-col layout */}
            {makersReviews.filter((r) => r.featured).length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {makersReviews.filter((r) => r.featured).map((review) => (
                  <SubmittedReviewCard
                    key={review.id}
                    review={review}
                    isDark={isDark}
                    headingColor={headingColor}
                    subColor={subColor}
                    isAdmin={isAdmin}
                    large
                    onToggle={isAdmin ? () => updateSubmittedReview(review.id, { visible: !review.visible }) : undefined}
                    onDelete={isAdmin ? () => deleteSubmittedReview(review.id) : undefined}
                    onMoveSection={isAdmin ? () => moveReviewSection(review.id) : undefined}
                    onUpdatePosition={isAdmin ? (pos) => updateSubmittedReview(review.id, { imagePosition: pos }) : undefined}
                    onToggleFeatured={isAdmin ? () => updateSubmittedReview(review.id, { featured: !review.featured }) : undefined}
                  />
                ))}
              </div>
            )}
            {/* Regular submitted reviews — 3-col grid */}
            {makersReviews.filter((r) => !r.featured).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {makersReviews.filter((r) => !r.featured).map((review) => (
                  <SubmittedReviewCard
                    key={review.id}
                    review={review}
                    isDark={isDark}
                    headingColor={headingColor}
                    subColor={subColor}
                    isAdmin={isAdmin}
                    onToggle={isAdmin ? () => updateSubmittedReview(review.id, { visible: !review.visible }) : undefined}
                    onDelete={isAdmin ? () => deleteSubmittedReview(review.id) : undefined}
                    onMoveSection={isAdmin ? () => moveReviewSection(review.id) : undefined}
                    onUpdatePosition={isAdmin ? (pos) => updateSubmittedReview(review.id, { imagePosition: pos }) : undefined}
                    onToggleFeatured={isAdmin ? () => updateSubmittedReview(review.id, { featured: !review.featured }) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Visitors section — submitted reviews from customers/visitors */}
        {(visitorsReviews.length > 0 || (isAdmin && submittedReviews.some((r) => {
          const s = (r as SubmittedReview & { sectionOverride?: string }).sectionOverride ?? getReviewSection(r.role);
          return s === "visitors";
        }))) && (
          <div className="mt-14">
            <SectionDivider
              label={data.visitorsHeading ?? "From Our Visitors"}
              isAdmin={isAdmin}
              onEdit={(v) => set("visitorsHeading", v)}
              isDark={isDark}
            />
            {/* Featured submitted reviews — large 2-col layout */}
            {visitorsReviews.filter((r) => r.featured).length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {visitorsReviews.filter((r) => r.featured).map((review) => (
                  <SubmittedReviewCard
                    key={review.id}
                    review={review}
                    isDark={isDark}
                    headingColor={headingColor}
                    subColor={subColor}
                    isAdmin={isAdmin}
                    large
                    onToggle={isAdmin ? () => updateSubmittedReview(review.id, { visible: !review.visible }) : undefined}
                    onDelete={isAdmin ? () => deleteSubmittedReview(review.id) : undefined}
                    onMoveSection={isAdmin ? () => moveReviewSection(review.id) : undefined}
                    onUpdatePosition={isAdmin ? (pos) => updateSubmittedReview(review.id, { imagePosition: pos }) : undefined}
                    onToggleFeatured={isAdmin ? () => updateSubmittedReview(review.id, { featured: !review.featured }) : undefined}
                  />
                ))}
              </div>
            )}
            {/* Regular submitted reviews — 3-col grid */}
            {visitorsReviews.filter((r) => !r.featured).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitorsReviews.filter((r) => !r.featured).map((review) => (
                  <SubmittedReviewCard
                    key={review.id}
                    review={review}
                    isDark={isDark}
                    headingColor={headingColor}
                    subColor={subColor}
                    isAdmin={isAdmin}
                    onToggle={isAdmin ? () => updateSubmittedReview(review.id, { visible: !review.visible }) : undefined}
                    onDelete={isAdmin ? () => deleteSubmittedReview(review.id) : undefined}
                    onMoveSection={isAdmin ? () => moveReviewSection(review.id) : undefined}
                    onUpdatePosition={isAdmin ? (pos) => updateSubmittedReview(review.id, { imagePosition: pos }) : undefined}
                    onToggleFeatured={isAdmin ? () => updateSubmittedReview(review.id, { featured: !review.featured }) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div
          className={`mt-20 text-center rounded-3xl p-12 ${isDark ? "bg-gray-800 border border-gray-700" : ""}`}
          style={isDark ? {} : { background: "linear-gradient(135deg, #fce7f3 0%, #dcfce7 100%)" }}
        >
          <div className={`w-14 h-14 flex items-center justify-center rounded-full mx-auto mb-4 ${isDark ? "bg-gray-700" : "bg-white"}`}>
            <i className="ri-chat-heart-line text-pink-500 text-2xl"></i>
          </div>
          {isEditing ? (
            <>
              <AdminEditField value={data.ctaLabel} onSave={(v) => set("ctaLabel", v)} className="mb-2" inputClassName="text-2xl font-extrabold text-center">
                <h2 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.ctaLabel}</h2>
              </AdminEditField>
              <AdminEditField value={data.ctaSubtext} onSave={(v) => set("ctaSubtext", v)} multiline className="mb-6" inputClassName="text-base text-center">
                <p className={`text-base mb-6 ${subColor}`}>{data.ctaSubtext}</p>
              </AdminEditField>
            </>
          ) : (
            <>
              <h2 className={`text-2xl font-extrabold mb-2 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{data.ctaLabel}</h2>
              <p className={`text-base mb-6 ${subColor}`}>{data.ctaSubtext}</p>
            </>
          )}
          <button
            onClick={() => navigate("/leave-a-review")}
            className="inline-flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3.5 rounded-full transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-star-line"></i>
            Leave a Review
          </button>
        </div>
      </main>

      {/* Admin visibility toggle */}
      {isAdmin && (
        <div className={`w-full border-t px-6 py-5 ${isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50"}`}>
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className={`font-semibold text-sm flex items-center gap-2 ${headingColor}`}>
                <i className="ri-shield-check-line text-green-400"></i>
                Admin — Page Visibility
              </p>
              <p className={`text-xs mt-0.5 ${subColor}`}>When hidden, this page is completely invisible — no "coming soon", no redirect.</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <span className={`text-sm ${subColor}`}>Page visible to public:</span>
              <button
                type="button"
                onClick={() => set("pageVisible", !data.pageVisible)}
                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${data.pageVisible ? "bg-green-500" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${data.pageVisible ? "translate-x-7" : "translate-x-1"}`}></span>
              </button>
              <span className={`font-bold text-sm ${data.pageVisible ? "text-green-500" : subColor}`}>
                {data.pageVisible ? "Live" : "Hidden"}
              </span>
            </label>
          </div>
        </div>
      )}

      <Footer />
      <AdminSaveBar />
    </div>
  );
}